"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

const floatingCards = [
{
  id: 1,
  className: "float-card-1",
  position: "top-[18%] right-[8%] lg:top-[18%] lg:right-[8%]",
  mobilePosition: "top-[15%] right-[4%]",
  label: "Experience",
  value: "20+ Years",
  sub: "Startups to public companies",
  icon: "🏆"
},
{
  id: 2,
  className: "float-card-2",
  position: "top-[52%] right-[4%]",
  mobilePosition: "hidden",
  label: "Scale",
  value: "450+",
  sub: "Team members led",
  icon: "🎯"
},
{
  id: 3,
  className: "float-card-3",
  position: "bottom-[22%] right-[14%]",
  mobilePosition: "hidden",
  label: "Scope",
  value: "Full Stack",
  sub: "Infrastructure to boardroom",
  icon: "📈"
}];


export default function HeroSection() {
  const headlineRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const el = headlineRef?.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(30px)";
    const timer = setTimeout(() => {
      el.style.transition = "opacity 1.2s cubic-bezier(0.16, 1, 0.3, 1), transform 1.2s cubic-bezier(0.16, 1, 0.3, 1)";
      el.style.opacity = "1";
      el.style.transform = "translateY(0)";
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section aria-label="Hero" className="relative min-h-screen flex items-center overflow-hidden bg-[#0C0C0E]">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0" aria-hidden="true">
        <AppImage
          src="https://images.unsplash.com/photo-1711320760690-73690d88d802"
          alt="Modern glass office building with city skyline at dusk, representing elite corporate environment"
          fill
          className="object-cover object-center"
          priority />
        
        {/* Multi-layer overlay for dark cinematic feel */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0C0C0E] via-[#0C0C0E]/85 to-[#0C0C0E]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C0E] via-transparent to-[#0C0C0E]/40" />
      </div>
      {/* Hero Glow */}
      <div aria-hidden="true" className="hero-glow absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 z-0" />
      {/* Content */}
      <div className="relative z-10 max-w-8xl mx-auto px-4 sm:px-6 lg:px-12 pt-24 sm:pt-28 md:pt-32 pb-16 sm:pb-20 md:pb-24 w-full">
        <div className="max-w-3xl">
          {/* Eyebrow */}
          <div
            className="inline-flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8 opacity-0"
            style={{
              animation: "count-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.4s forwards"
            }}>
            
            <div className="w-6 sm:w-8 h-[1px] bg-[#B8986A]" />
            <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.3em] sm:tracking-[0.35em] uppercase text-[#B8986A]">
              JAMO LLC — Technology Consulting
            </span>
          </div>

          {/* Headline */}
          <h1
            ref={headlineRef}
            className="font-fraunces text-[clamp(2.5rem,10vw,6.5rem)] sm:text-[clamp(3rem,7vw,6.5rem)] font-light leading-[1.05] sm:leading-[1.0] tracking-tight text-[#F2EFE9] mb-6 sm:mb-8">
            
            Server Room
            <br />
            to the
            <br />
            <span className="italic text-gradient-gold">Boardroom.</span>
          </h1>

          {/* Sub */}
          <p
            className="text-[#A09D97] text-base sm:text-lg md:text-xl font-light leading-relaxed max-w-xl mb-8 sm:mb-12 opacity-0"
            style={{
              animation: "count-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.8s forwards"
            }}>
            
            Technology consulting led by operators who have built teams, designed infrastructure, and run operations at scale—from early-stage startups to publicly traded enterprises.
          </p>

          {/* CTAs */}
          <div
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 opacity-0"
            style={{
              animation: "count-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) 1.0s forwards"
            }}>
            
            <a href="#contact" className="btn-primary text-center" aria-label="Contact us to start a conversation">
              <span>Let&apos;s Talk</span>
            </a>
            <a href="#services" className="btn-secondary text-center" aria-label="View our services">
              Our Services
            </a>
          </div>
        </div>
      </div>
      {/* Floating Credential Cards */}
      {floatingCards?.map((card) =>
      <div
        key={card?.id}
        role="presentation"
        aria-hidden="true"
        className={`absolute z-20 ${card?.position} ${card?.id !== 1 ? "hidden lg:block" : ""} ${card?.className}`}
        style={{
          opacity: 0,
          animation: `count-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${0.8 + card?.id * 0.2}s forwards, ${
          card?.className === "float-card-1" ? "float-gentle 6s cubic-bezier(0.45, 0.05, 0.55, 0.95) 2s infinite" :
          card?.className === "float-card-2" ? "float-gentle-reverse 7s cubic-bezier(0.45, 0.05, 0.55, 0.95) 3s infinite" : "float-slow 8s cubic-bezier(0.45, 0.05, 0.55, 0.95) 4s infinite"}`

        }}>
        
          <div className="glass-card rounded-sm p-3 sm:p-4 min-w-[160px] sm:min-w-[180px] glow-gold">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg sm:text-xl">{card?.icon}</span>
              <span className="text-[8px] sm:text-[9px] font-bold tracking-[0.2em] uppercase text-[#6B6864]">
                {card?.label}
              </span>
            </div>
            <div className="text-[#F2EFE9] font-fraunces font-medium text-lg sm:text-xl mb-0.5">
              {card?.value}
            </div>
            <div className="text-[#6B6864] text-[9px] sm:text-[10px] font-medium">{card?.sub}</div>
          </div>
        </div>
      )}
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 sm:bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 opacity-40" aria-hidden="true">
        <span className="text-[9px] font-bold tracking-[0.3em] uppercase text-[#A09D97]">
          Scroll
        </span>
        <div className="w-[1px] h-12 bg-gradient-to-b from-[#A09D97] to-transparent" />
      </div>
    </section>);

}